import React, { useEffect, useMemo, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import {
  ChevronRight,
  DollarSign,
  FileSpreadsheet,
  LayoutDashboard,
  LogOut,
  Menu,
  Moon,
  PanelLeft,
  PenSquare,
  Settings,
  Sun,
  Upload,
  Users,
  X,
} from "lucide-react";
import { AdminPortalContext } from "./AdminPortalContext";
import { fetchAdminBlogs } from "../../../../utils/blogApi";
import { API_ROOT } from "../../../../utils/apiConfig";
const ADMIN_TOKEN_KEY = "busigrow_admin_token";
const ADMIN_THEME_KEY = "busigrow_admin_theme";

const navigationItems = [
  {
    to: "dashboard",
    label: "Dashboard",
    icon: LayoutDashboard,
    description: "Overview and quick actions",
  },
  {
    to: "submissions",
    label: "Submissions",
    icon: Users,
    description: "Contact form leads",
  },
  {
    to: "exports",
    label: "Exports",
    icon: Upload,
    description: "CSV and PDF downloads",
  },
  {
    to: "blogs",
    label: "Blogs",
    icon: PenSquare,
    description: "Manage BusiMag posts",
  },
  {
    to: "pricing",
    label: "Pricing",
    icon: DollarSign,
    description: "Manage pricing plans and FAQs",
  },
  {
    to: "settings",
    label: "Settings",
    icon: Settings,
    description: "Theme and admin profile",
  },
];

const getPageTitle = (pathname) => {
  if (pathname.endsWith("/dashboard")) {
    return "Dashboard";
  }

  if (pathname.endsWith("/submissions")) {
    return "Contact Submissions";
  }

  if (pathname.endsWith("/exports")) {
    return "Export Center";
  }

  if (pathname.endsWith("/blogs")) {
    return "Blog Management";
  }

  if (pathname.endsWith("/pricing")) {
    return "Pricing Management";
  }

  if (pathname.endsWith("/settings")) {
    return "Settings";
  }

  return "Admin Portal";
};

const formatDate = (value) => new Date(value).toLocaleString();

const AdminLoginScreen = ({
  errorMessage,
  isAuthenticating,
  loginForm,
  onChange,
  onSubmit,
}) => (
  <div className="min-h-screen bg-[#f4f7fb] text-slate-900 flex items-center justify-center px-4 py-10">
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div className="absolute top-0 left-0 w-72 h-72 bg-sky-100 rounded-full blur-3xl opacity-80" />
      <div className="absolute bottom-0 right-0 w-80 h-80 bg-amber-100 rounded-full blur-3xl opacity-70" />
    </div>

    <div className="relative w-full max-w-[1040px] grid grid-cols-1 lg:grid-cols-[1.15fr_0.85fr] overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_30px_90px_rgba(15,23,42,0.10)]">
      <div className="hidden lg:flex flex-col justify-between bg-[linear-gradient(160deg,#fff8ef_0%,#f6fbff_45%,#eef4ff_100%)] p-10 border-r border-slate-200">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm">
            <PanelLeft className="h-4 w-4 text-sky-600" />
            Hidden Admin Portal
          </div>
          <h1 className="mt-8 max-w-md text-4xl font-bold leading-tight text-slate-900">
            Proper admin workspace for leads, exports, and future operations.
          </h1>
          <p className="mt-5 max-w-md text-base leading-7 text-slate-600">
            Light-first admin UI with clear contrast, structured navigation, and
            room to integrate analytics, automation, and user management later.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4">
          {[
            "Sidebar navigation with dedicated pages",
            "CSV and PDF exports from the admin area",
            "Light theme by default, dark mode optional",
          ].map((item) => (
            <div
              key={item}
              className="rounded-2xl border border-white/70 bg-white/90 px-5 py-4 text-sm font-medium text-slate-700 shadow-sm"
            >
              {item}
            </div>
          ))}
        </div>
      </div>

      <div className="p-6 sm:p-8 lg:p-10">
        <div className="mx-auto w-full max-w-md">
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-sky-700">
            Restricted Access
          </p>
          <h2 className="mt-3 text-3xl font-bold text-slate-900">
            Admin Sign In
          </h2>
          <p className="mt-2 text-sm leading-6 text-slate-500">
            Authenticate to access the hidden admin panel and manage
            submissions.
          </p>

          <form className="mt-8 space-y-5" onSubmit={onSubmit}>
            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-700">
                Admin Email
              </label>
              <input
                type="email"
                required
                value={loginForm.email}
                onChange={(event) => onChange("email", event.target.value)}
                className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-sky-500 focus:ring-4 focus:ring-sky-100"
                placeholder="admin@busigrow.in"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-700">
                Password
              </label>
              <input
                type="password"
                required
                value={loginForm.password}
                onChange={(event) => onChange("password", event.target.value)}
                className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-sky-500 focus:ring-4 focus:ring-sky-100"
                placeholder="Enter admin password"
              />
            </div>

            {errorMessage && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {errorMessage}
              </div>
            )}

            <button
              type="submit"
              disabled={isAuthenticating}
              className="w-full rounded-2xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-slate-900/10 transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isAuthenticating ? "Signing in..." : "Login to Admin"}
            </button>
          </form>

          <div className="mt-8 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-xs leading-6 text-slate-500">
            Access path remains intentionally deep. Keep credentials in the
            backend environment and rotate them before production use.
          </div>
        </div>
      </div>
    </div>
  </div>
);

const AdminPortalRoot = () => {
  const [token, setToken] = useState(
    localStorage.getItem(ADMIN_TOKEN_KEY) || "",
  );
  const [theme, setTheme] = useState(
    localStorage.getItem(ADMIN_THEME_KEY) || "light",
  );
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [submissions, setSubmissions] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [adminProfile, setAdminProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [activeExportFormat, setActiveExportFormat] = useState("");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    localStorage.setItem(ADMIN_THEME_KEY, theme);
  }, [theme]);

  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname]);

  const fetchSubmissions = async (authToken) => {
    setIsLoading(true);
    setErrorMessage("");

    try {
      const response = await fetch(`${API_ROOT}/admin/contact-submissions`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      const data = await response.json();

      if (!response.ok || !data?.success) {
        throw new Error(data?.message || "Failed to load submissions");
      }

      setSubmissions(Array.isArray(data.data) ? data.data : []);
    } catch (error) {
      setErrorMessage(error.message || "Failed to load submissions");
      if (
        error.message?.toLowerCase().includes("token") ||
        error.message?.toLowerCase().includes("admin")
      ) {
        localStorage.removeItem(ADMIN_TOKEN_KEY);
        setToken("");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAdminProfile = async (authToken) => {
    try {
      const response = await fetch(`${API_ROOT}/admin/me`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      const data = await response.json();

      if (!response.ok || !data?.success) {
        throw new Error(data?.message || "Failed to load admin profile");
      }

      setAdminProfile(data.data || null);
    } catch (error) {
      setErrorMessage(error.message || "Failed to load admin profile");
    }
  };

  const fetchBlogs = async (authToken) => {
    try {
      const nextBlogs = await fetchAdminBlogs(authToken);
      setBlogs(nextBlogs);
    } catch (error) {
      setErrorMessage(error.message || "Failed to load blogs");
      if (error.message?.toLowerCase().includes("token")) {
        localStorage.removeItem(ADMIN_TOKEN_KEY);
        setToken("");
      }
    }
  };

  useEffect(() => {
    if (!token) {
      return;
    }

    fetchAdminProfile(token);
    fetchSubmissions(token);
    fetchBlogs(token);
  }, [token]);

  const handleLogin = async (event) => {
    event.preventDefault();
    setErrorMessage("");
    setIsAuthenticating(true);

    try {
      const response = await fetch(`${API_ROOT}/admin/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(loginForm),
      });
      const data = await response.json();

      if (!response.ok || !data?.success) {
        throw new Error(data?.message || "Admin login failed");
      }

      const nextToken = data?.data?.token;
      if (!nextToken) {
        throw new Error("Admin token missing from response");
      }

      localStorage.setItem(ADMIN_TOKEN_KEY, nextToken);
      setToken(nextToken);
    } catch (error) {
      setErrorMessage(error.message || "Admin login failed");
    } finally {
      setIsAuthenticating(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    setToken("");
    setAdminProfile(null);
    setSubmissions([]);
    setBlogs([]);
  };

  const downloadExport = async (format) => {
    setErrorMessage("");
    setActiveExportFormat(format);

    try {
      const response = await fetch(
        `${API_ROOT}/admin/contact-submissions/export?format=${format}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        let message = `Failed to export ${format.toUpperCase()}`;

        try {
          const data = await response.json();
          message = data?.message || message;
        } catch {
          // Ignore JSON parse errors for binary responses.
        }

        throw new Error(message);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const disposition = response.headers.get("content-disposition") || "";
      const fileNameMatch = disposition.match(/filename="?([^\"]+)"?/i);
      const fileName = fileNameMatch?.[1] || `contact-submissions.${format}`;

      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setErrorMessage(
        error.message || `Failed to export ${format.toUpperCase()}`,
      );
    } finally {
      setActiveExportFormat("");
    }
  };

  const pageTitle = getPageTitle(location.pathname);
  const pageDescription =
    navigationItems.find((item) => location.pathname.endsWith(item.to))
      ?.description || "Admin operations workspace";

  const totalSubmissions = submissions.length;
  const todayCount = submissions.filter((item) => {
    const created = new Date(item.createdAt);
    const today = new Date();

    return created.toDateString() === today.toDateString();
  }).length;
  const lastSevenDaysCount = submissions.filter((item) => {
    const created = new Date(item.createdAt).getTime();
    return Date.now() - created <= 7 * 24 * 60 * 60 * 1000;
  }).length;
  const topInterest = (() => {
    const counter = submissions.reduce((accumulator, item) => {
      accumulator[item.interest] = (accumulator[item.interest] || 0) + 1;
      return accumulator;
    }, {});
    const [label] =
      Object.entries(counter).sort((left, right) => right[1] - left[1])[0] ||
      [];
    return label || "No submissions yet";
  })();

  const portalContext = useMemo(
    () => ({
      activeExportFormat,
      adminProfile,
      downloadExport,
      errorMessage,
      fetchSubmissions,
      formatDate,
      isLoading,
      pageTitle,
      setErrorMessage,
      setTheme,
      submissions,
      blogs,
      fetchBlogs,
      theme,
      token,
      summary: {
        lastSevenDaysCount,
        todayCount,
        topInterest,
        totalSubmissions,
      },
    }),
    [
      activeExportFormat,
      adminProfile,
      blogs,
      errorMessage,
      isLoading,
      lastSevenDaysCount,
      pageTitle,
      submissions,
      theme,
      todayCount,
      token,
      topInterest,
      totalSubmissions,
    ],
  );

  if (!token) {
    return (
      <AdminLoginScreen
        errorMessage={errorMessage}
        isAuthenticating={isAuthenticating}
        loginForm={loginForm}
        onChange={(field, value) =>
          setLoginForm((previous) => ({ ...previous, [field]: value }))
        }
        onSubmit={handleLogin}
      />
    );
  }

  const isDarkTheme = theme === "dark";

  return (
    <div
      className={
        isDarkTheme
          ? "min-h-screen bg-slate-950 text-slate-100"
          : "min-h-screen bg-[#f6f8fb] text-slate-900"
      }
    >
      <div className={isDarkTheme ? "flex min-h-screen" : "flex min-h-screen"}>
        {isSidebarOpen && (
          <button
            type="button"
            className="fixed inset-0 z-30 bg-slate-950/30 backdrop-blur-sm lg:hidden"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}

        <aside
          className={`fixed inset-y-0 left-0 z-40 w-[290px] transform border-r transition-transform duration-300 lg:static lg:translate-x-0 ${
            isDarkTheme
              ? "border-slate-800 bg-slate-900"
              : "border-slate-200 bg-white"
          } ${isSidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
        >
          <div className="flex h-full flex-col">
            <div
              className={`border-b px-6 py-6 ${isDarkTheme ? "border-slate-800" : "border-slate-200"}`}
            >
              <div className="flex items-center justify-between gap-3 lg:justify-start">
                <Link to="dashboard" className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-lg shadow-slate-900/15">
                    BG
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.22em] text-sky-600">
                      BusiGrow
                    </p>
                    <p className="text-lg font-bold">Admin Panel</p>
                  </div>
                </Link>
                <button
                  type="button"
                  className={`inline-flex h-10 w-10 items-center justify-center rounded-xl lg:hidden ${isDarkTheme ? "bg-slate-800 text-slate-200" : "bg-slate-100 text-slate-600"}`}
                  onClick={() => setIsSidebarOpen(false)}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div
                className={`mt-6 rounded-2xl border px-4 py-4 ${isDarkTheme ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-slate-50"}`}
              >
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">
                  Logged In As
                </p>
                <p className="mt-2 text-sm font-semibold">
                  {adminProfile?.email || "Admin"}
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  Hidden route access only
                </p>
              </div>
            </div>

            <nav className="flex-1 space-y-2 px-4 py-6">
              {navigationItems.map((item) => {
                const Icon = item.icon;

                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      `group flex items-center gap-3 rounded-2xl px-4 py-3.5 transition ${
                        isActive
                          ? isDarkTheme
                            ? "bg-sky-500/15 text-sky-300"
                            : "bg-sky-50 text-sky-700 shadow-sm"
                          : isDarkTheme
                            ? "text-slate-300 hover:bg-slate-800 hover:text-white"
                            : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                      }`
                    }
                  >
                    <Icon className="h-5 w-5 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold">{item.label}</p>
                      <p
                        className={`text-xs ${isDarkTheme ? "text-slate-500" : "text-slate-400"}`}
                      >
                        {item.description}
                      </p>
                    </div>
                  </NavLink>
                );
              })}
            </nav>

            <div
              className={`border-t px-4 py-5 ${isDarkTheme ? "border-slate-800" : "border-slate-200"}`}
            >
              <button
                type="button"
                onClick={handleLogout}
                className={`flex w-full items-center justify-center gap-2 rounded-2xl px-4 py-3 text-sm font-semibold transition ${isDarkTheme ? "bg-slate-800 text-slate-100 hover:bg-slate-700" : "bg-slate-900 text-white hover:bg-slate-800"}`}
              >
                <LogOut className="h-4 w-4" />
                Logout
              </button>
            </div>
          </div>
        </aside>

        <div className="flex min-h-screen flex-1 flex-col">
          <header
            className={`sticky top-0 z-20 border-b backdrop-blur ${isDarkTheme ? "border-slate-800 bg-slate-950/90" : "border-slate-200 bg-white/90"}`}
          >
            <div className="flex items-center justify-between gap-4 px-4 py-4 sm:px-6 lg:px-8">
              <div className="flex min-w-0 items-center gap-3">
                <button
                  type="button"
                  onClick={() => setIsSidebarOpen(true)}
                  className={`inline-flex h-11 w-11 items-center justify-center rounded-2xl lg:hidden ${isDarkTheme ? "bg-slate-800 text-slate-100" : "bg-slate-100 text-slate-700"}`}
                >
                  <Menu className="h-5 w-5" />
                </button>

                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">
                    <span>Admin</span>
                    <ChevronRight className="h-3 w-3" />
                    <span>{pageTitle}</span>
                  </div>
                  <h1 className="mt-1 truncate text-2xl font-bold">
                    {pageTitle}
                  </h1>
                  <p
                    className={`mt-1 hidden text-sm sm:block ${isDarkTheme ? "text-slate-400" : "text-slate-500"}`}
                  >
                    {pageDescription}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() =>
                    setTheme((previous) =>
                      previous === "light" ? "dark" : "light",
                    )
                  }
                  className={`inline-flex items-center gap-2 rounded-2xl border px-4 py-2.5 text-sm font-semibold transition ${isDarkTheme ? "border-slate-700 bg-slate-900 text-slate-100 hover:bg-slate-800" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}
                >
                  {isDarkTheme ? (
                    <Sun className="h-4 w-4" />
                  ) : (
                    <Moon className="h-4 w-4" />
                  )}
                  {isDarkTheme ? "Light Mode" : "Dark Mode"}
                </button>
              </div>
            </div>
          </header>

          <main className="flex-1 px-4 py-6 sm:px-6 lg:px-8">
            {errorMessage && (
              <div
                className={`mb-6 rounded-2xl border px-4 py-3 text-sm ${isDarkTheme ? "border-rose-500/30 bg-rose-500/10 text-rose-200" : "border-rose-200 bg-rose-50 text-rose-700"}`}
              >
                {errorMessage}
              </div>
            )}

            <AdminPortalContext.Provider value={portalContext}>
              <Outlet />
            </AdminPortalContext.Provider>
          </main>
        </div>
      </div>
    </div>
  );
};

export default AdminPortalRoot;
